import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  Animated,
  Platform,
  TextInput,
  ScrollView,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import {
  ADMIN_SETTING_SCREEN_COLORS,
  BACKGROUND_COLORS,
  BORDER_COLORS,
  BRAND_COLORS,
  SHADOW_COLORS,
  STATUS_COLORS,
  TEXT_COLORS,
} from "../../style/colors.js";
import { getActiveMosWithProcessDates, markProcessAsCompleted } from "../../services/api/operations/processAlerts/ProcessAlertsApi.js";
import { validateMultipleMos } from "../../services/utils/processValidation.js";
import { getErrorMessage, notify } from "../../services/ui/notify";

const ALERT_CONFIG = {
  ANIMATION_CONFIG: {
    FADE_DURATION: 300,
    SCALE_DURATION: 200,
  },
};

// Mapeo de códigos de proceso a procesos originales según PROCESS_MATCHING
// Usa el formato exacto que está en processValidation.js
const getOriginalProcess = (processCode, userTeam) => {
  const PROCESS_MATCHING = {
    A: [
      { team: "fundicion", tipo: "Clasif." },
      { team: "fundicion", tipo: "Mecanizado." },
    ],
    B: [{ team: null, tipo: "ASIGNAR" }],
    C: [{ team: null, tipo: "REPASO" }],
    D: [{ team: null, tipo: "BOMBOS" }],
    E: [{ team: null, tipo: "Bagging Completo" }],
    F: [{ team: "engastado", tipo: "PIEDRAS" }],
    G: [{ team: null, tipo: "PULIDO" }],
    H: [{ team: null, tipo: "BAÑOS" }],
    I: [{ team: null, tipo: "GRABADO" }],
    J: [{ team: null, tipo: "CALIDAD" }],
    K: [{ team: null, tipo: "Contraste" }],
    L: [{ team: null, tipo: "Term." }],
  };

  const matchingRules = PROCESS_MATCHING[processCode];
  if (!matchingRules || matchingRules.length === 0) {
    return null;
  }

  // Para Fundición (A), determinar si es Clasif o Mecanizado
  // Por defecto usamos Clasif, pero puedes ajustar la lógica según necesites
  if (processCode === "A") {
    // Si hay lógica específica para determinar Clasif vs Mecanizado, añádela aquí
    // Por ahora, usamos Clasif por defecto
    return matchingRules[0].tipo; // "Clasif"
  }

  // Para otros procesos, devolver el primer tipo (ya que solo hay uno)
  return matchingRules[0].tipo;
};

const ProcessAlertRow = React.memo(({ item, onMarkCompleted, onRefresh, userTeam, userName }) => {
  const [isMarking, setIsMarking] = useState(false);

  const handleMarkCompleted = async () => {
    setIsMarking(true);
    try {
      // Obtener el proceso original
      const procesoOriginal = getOriginalProcess(item.processCode, userTeam);
      
      // Obtener la fecha actual
      const fechaActual = new Date().toISOString();
      
      // Calcular PO desde MO: quitar los últimos 4 caracteres
      const po = item.mo ? item.mo.slice(0, -4) : null;
      
      // Llamar a la API con todos los datos
      const result = await markProcessAsCompleted(
        item.mo,
        po,
        item.processCode,
        procesoOriginal,
        fechaActual,
        userName,
        userTeam
      );
      
      if (result && result.status === "ok") {
        notify.success(
          result.message || "El proceso ha sido marcado como realizado."
        );
        // Recargar las alertas desde el servidor
        if (onRefresh) {
          await onRefresh();
        }
        onMarkCompleted(item.mo, item.processCode);
      } else {
        notify.error(
          result?.message || "No se pudo marcar el proceso como realizado."
        );
      }
    } catch (error) {
      console.error("Error al marcar proceso:", error);
      notify.error(getErrorMessage(error, "Ocurrió un error al marcar el proceso."));
    } finally {
      setIsMarking(false);
    }
  };

  // Formatear fecha del pedido
  const formatFechaPedido = () => {
    if (!item.fechaPedido) return "N/A";
    if (typeof item.fechaPedido === "object" && item.fechaPedido.date) {
      const date = new Date(item.fechaPedido.date);
      return date.toLocaleDateString("es-ES");
    }
    if (typeof item.fechaPedido === "string") {
      const date = new Date(item.fechaPedido);
      return date.toLocaleDateString("es-ES");
    }
    return "N/A";
  };

  // Formatear fecha de entrega
  const formatFechaEntrega = () => {
    if (!item.fechaEntrega) return "N/A";
    if (typeof item.fechaEntrega === "object" && item.fechaEntrega.date) {
      const date = new Date(item.fechaEntrega.date);
      return date.toLocaleDateString("es-ES");
    }
    if (typeof item.fechaEntrega === "string") {
      const date = new Date(item.fechaEntrega);
      return date.toLocaleDateString("es-ES");
    }
    return "N/A";
  };

  // Calcular días de retraso
  const diasDesdePedido = item.daysSinceOrder !== undefined ? item.daysSinceOrder : 0;
  const diasDesdeUltimoProceso = item.days !== undefined ? item.days : 0;
  const diasEsperados = item.expectedDays || 0;
  const diasRetrasoPedido = Math.max(0, diasDesdePedido - diasEsperados);
  const diasRetrasoUltimo = Math.max(0, diasDesdeUltimoProceso - diasEsperados);
  
  // Determinar color según días de retraso absoluto (para el indicador)
  const getIndicatorColor = () => {
    const diasRetraso = Math.max(diasRetrasoPedido, diasRetrasoUltimo);
    if (diasRetraso > 5) {
      return "#dc2626"; // Rojo: más de 5 días de retraso
    } else if (diasRetraso > 0) {
      return "#f59e0b"; // Naranja: entre 1 y 5 días de retraso
    }
    return "#9bb1bb"; // Gris: sin retraso (aunque no debería aparecer en alertas)
  };

  // Determinar color para días desde F. Pedido
  const getDiasPedidoColor = () => {
    if (diasRetrasoPedido > 5) {
      return "#dc2626"; // Rojo
    } else if (diasRetrasoPedido > 0) {
      return "#f59e0b"; // Naranja
    }
    return "#000000"; // Negro por defecto
  };

  // Determinar color para días desde Últ. Proceso
  const getDiasUltimoColor = () => {
    if (diasRetrasoUltimo > 5) {
      return "#dc2626"; // Rojo
    } else if (diasRetrasoUltimo > 0) {
      return "#f59e0b"; // Naranja
    }
    return "#000000"; // Negro por defecto
  };

  return (
    <View style={styles.tableRow}>
      <View style={styles.tableCellMO}>
        <View style={styles.moContainer}>
          <View
            style={[
              styles.priorityIndicator,
              {
                backgroundColor: getIndicatorColor(),
              },
            ]}
          />
          <Text style={styles.moText}>{item.mo}</Text>
        </View>
      </View>
      <View style={styles.tableCellCliente}>
        <Text style={styles.cellText} numberOfLines={1}>{item.cliente || "N/A"}</Text>
      </View>
      <View style={styles.tableCellFecha}>
        <Text style={styles.cellText}>{formatFechaPedido()}</Text>
      </View>
      <View style={styles.tableCellFechaEntrega}>
        <Text style={styles.cellText}>{formatFechaEntrega()}</Text>
      </View>
      <View style={styles.tableCellProceso}>
        <Text style={styles.cellText}>{item.processName}</Text>
      </View>
      <View style={styles.tableCellDiasPedido}>
        <Text style={[styles.cellText, { color: getDiasPedidoColor() }]}>
          {item.daysSinceOrder !== undefined ? item.daysSinceOrder : 0}
        </Text>
      </View>
      <View style={styles.tableCellDiasUltimo}>
        <Text style={[styles.cellText, { color: getDiasUltimoColor() }]}>
          {item.days !== undefined ? item.days : 0}
        </Text>
      </View>
      <View style={styles.tableCellDiasEsperados}>
        <Text style={styles.cellText}>{item.expectedDays || "N/A"}</Text>
      </View>
      <View style={styles.tableCellAction}>
        <TouchableOpacity
          onPress={handleMarkCompleted}
          disabled={isMarking}
          style={[styles.markCompletedButton, isMarking && styles.markCompletedButtonDisabled]}>
          <MaterialCommunityIcons
            name={isMarking ? "loading" : "check-circle"}
            size={16}
            color={isMarking ? "#9bb1bb" : "#174045"}
          />
          <Text style={[styles.markCompletedText, isMarking && styles.markCompletedTextDisabled]}>
            {isMarking ? "..." : "Realizado"}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
});

// Componente de filtro desplegable
const FilterDropdown = React.memo(({ 
  label, 
  options, 
  selectedOptions, 
  onToggleOption, 
  onSelectAll, 
  onDeselectAll,
  onPrint,
  isOpen,
  onToggleOpen
}) => {
  const dropdownRef = React.useRef(null);
  const [searchFilter, setSearchFilter] = useState("");

  // Filtrar opciones según búsqueda
  const filteredOptions = useMemo(() => {
    if (!searchFilter.trim()) {
      return options;
    }
    const searchLower = searchFilter.toLowerCase();
    return options.filter(option => 
      option.toLowerCase().includes(searchLower)
    );
  }, [options, searchFilter]);

  // Cerrar dropdown cuando se hace clic fuera (solo en web)
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          if (isOpen) {
            onToggleOpen();
            setSearchFilter("");
          }
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onToggleOpen]);

  // Obtener texto a mostrar en el botón
  const getButtonText = () => {
    if (selectedOptions.size === 0) {
      return label;
    }
    if (selectedOptions.size === 1) {
      return Array.from(selectedOptions)[0];
    }
    return `${label} (${selectedOptions.size})`;
  };

  return (
    <View style={styles.filterContainer} ref={dropdownRef}>
      <TouchableOpacity
        onPress={onToggleOpen}
        style={styles.filterButton}>
        <Text style={styles.filterButtonText} numberOfLines={1}>
          {getButtonText()}
        </Text>
        <MaterialCommunityIcons
          name={isOpen ? "chevron-up" : "chevron-down"}
          size={18}
          color="#174045"
        />
      </TouchableOpacity>
      
      {isOpen && (
        <View style={styles.filterDropdown}>
          {/* Buscador dentro del dropdown */}
          <View style={styles.filterSearchContainer}>
            <MaterialCommunityIcons
              name='magnify'
              size={18}
              color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
              style={styles.filterSearchIcon}
            />
            <TextInput
              style={styles.filterSearchInput}
              placeholder={`Buscar ${label.toLowerCase()}...`}
              placeholderTextColor={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
              value={searchFilter}
              onChangeText={setSearchFilter}
            />
            {searchFilter.length > 0 && (
              <TouchableOpacity
                onPress={() => setSearchFilter("")}
                style={styles.filterClearButton}>
                <MaterialCommunityIcons
                  name='close-circle'
                  size={18}
                  color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                />
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.filterActions}>
            <TouchableOpacity onPress={onSelectAll} style={styles.filterActionButton}>
              <Text style={styles.filterActionText}>Seleccionar todos</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={onDeselectAll} style={styles.filterActionButton}>
              <Text style={styles.filterActionText}>Deseleccionar todos</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.filterOptions} nestedScrollEnabled>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <TouchableOpacity
                  key={option}
                  onPress={() => onToggleOption(option)}
                  style={styles.filterOption}>
                  <MaterialCommunityIcons
                    name={selectedOptions.has(option) ? "checkbox-marked" : "checkbox-blank-outline"}
                    size={20}
                    color={selectedOptions.has(option) ? "#174045" : "#9bb1bb"}
                  />
                  <Text style={[
                    styles.filterOptionText,
                    selectedOptions.has(option) && styles.filterOptionTextSelected
                  ]}>
                    {option}
                  </Text>
                </TouchableOpacity>
              ))
            ) : (
              <View style={styles.filterOption}>
                <Text style={styles.filterOptionText}>No se encontraron resultados</Text>
              </View>
            )}
          </ScrollView>
        </View>
      )}
    </View>
  );
});

export default function ComponentProcessAlertsModal({ visible, onClose }) {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [alertsLoaded, setAlertsLoaded] = useState(false);
  const [selectedClientes, setSelectedClientes] = useState(new Set());
  const [selectedTiposOrden, setSelectedTiposOrden] = useState(new Set());
  const [isClientesDropdownOpen, setIsClientesDropdownOpen] = useState(false);
  const [isTiposOrdenDropdownOpen, setIsTiposOrdenDropdownOpen] = useState(false);
  
  // Obtener usuario y team desde Redux
  const userName = useSelector((state) => state.user.name);
  const userTeam = useSelector((state) => state.user.team);

  // Animaciones
  const fadeAnim = useState(new Animated.Value(0))[0];
  const scaleAnim = useState(new Animated.Value(0.8))[0];

  // Obtener opciones únicas de clientes y tipos de orden
  const clientesOptions = useMemo(() => {
    const clientesSet = new Set();
    alerts.forEach((item) => {
      if (item.cliente && item.cliente !== "N/A") {
        clientesSet.add(item.cliente);
      }
    });
    return Array.from(clientesSet).sort();
  }, [alerts]);

  const tiposOrdenOptions = useMemo(() => {
    const tiposSet = new Set();
    alerts.forEach((item) => {
      if (item.tipo_orden && item.tipo_orden !== "N/A") {
        tiposSet.add(item.tipo_orden);
      }
    });
    return Array.from(tiposSet).sort();
  }, [alerts]);

  // Inicializar filtro por defecto con "SEVEN DAYS ORDERS" cuando se cargan las alertas
  useEffect(() => {
    if (alerts.length > 0 && selectedTiposOrden.size === 0 && alertsLoaded) {
      // Buscar si existe "SEVEN DAYS ORDERS" o variaciones similares
      const tiposSet = new Set();
      alerts.forEach((item) => {
        if (item.tipo_orden && item.tipo_orden !== "N/A") {
          tiposSet.add(item.tipo_orden);
        }
      });
      const tiposArray = Array.from(tiposSet).sort();
      
      const sevenDaysVariations = tiposArray.filter(tipo => 
        tipo.toLowerCase().includes("seven") || 
        tipo.toLowerCase().includes("7 days") ||
        tipo.toLowerCase().includes("7dias")
      );
      
      if (sevenDaysVariations.length > 0) {
        // Usar la primera variación encontrada
        setSelectedTiposOrden(new Set([sevenDaysVariations[0]]));
      }
    }
  }, [alerts, alertsLoaded, selectedTiposOrden.size]);

  // Filtrar alertas según búsqueda y filtros
  const filteredAlerts = useMemo(() => {
    let filtered = alerts;

    // Filtro por búsqueda
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const mo = (item.mo || "").toLowerCase();
        const cliente = (item.cliente || "").toLowerCase();
        const processName = (item.processName || "").toLowerCase();
        return mo.includes(searchLower) || 
               cliente.includes(searchLower) || 
               processName.includes(searchLower);
      });
    }

    // Filtro por clientes
    if (selectedClientes.size > 0) {
      filtered = filtered.filter((item) => 
        selectedClientes.has(item.cliente)
      );
    }

    // Filtro por tipos de orden
    if (selectedTiposOrden.size > 0) {
      filtered = filtered.filter((item) => 
        selectedTiposOrden.has(item.tipo_orden)
      );
    }

    return filtered;
  }, [alerts, searchTerm, selectedClientes, selectedTiposOrden]);

  // Handlers para filtros
  const handleToggleCliente = useCallback((cliente) => {
    setSelectedClientes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cliente)) {
        newSet.delete(cliente);
      } else {
        newSet.add(cliente);
      }
      return newSet;
    });
  }, []);

  const handleToggleTipoOrden = useCallback((tipoOrden) => {
    setSelectedTiposOrden((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tipoOrden)) {
        newSet.delete(tipoOrden);
      } else {
        newSet.add(tipoOrden);
      }
      return newSet;
    });
  }, []);

  const handleSelectAllClientes = useCallback(() => {
    setSelectedClientes(new Set(clientesOptions));
  }, [clientesOptions]);

  const handleDeselectAllClientes = useCallback(() => {
    setSelectedClientes(new Set());
  }, []);

  const handleSelectAllTiposOrden = useCallback(() => {
    setSelectedTiposOrden(new Set(tiposOrdenOptions));
  }, [tiposOrdenOptions]);

  const handleDeselectAllTiposOrden = useCallback(() => {
    setSelectedTiposOrden(new Set());
  }, []);

  // Eliminar los handlers de imprimir de los filtros ya que ahora se usa el botón principal

  // Cargar alertas
  const fetchAlerts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getActiveMosWithProcessDates();
      
      // El backend devuelve { status: "ok", data: { "mo1": {...}, "mo2": {...} } }
      // o directamente el objeto de datos
      const mosData = response?.data || response;
      
      if (mosData) {
        const validatedAlerts = validateMultipleMos(mosData);
        setAlerts(validatedAlerts);
      } else {
        setAlerts([]);
      }
      
      setAlertsLoaded(true);
    } catch (error) {
      console.error("Error al obtener alertas de procesos:", error);
      setAlertsLoaded(true);
      notify.error(
        getErrorMessage(
          error,
          "No se pudieron cargar las alertas de procesos. Inténtalo nuevamente."
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Función para recargar alertas (se usa después de marcar como completado)
  const refreshAlerts = useCallback(async () => {
    try {
      const response = await getActiveMosWithProcessDates();
      const mosData = response?.data || response;
      
      if (mosData) {
        const validatedAlerts = validateMultipleMos(mosData);
        setAlerts(validatedAlerts);
      } else {
        setAlerts([]);
      }
    } catch (error) {
      console.error("Error al recargar alertas:", error);
    }
  }, []);

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (visible && !alertsLoaded) {
      fetchAlerts();
    }
  }, [visible, alertsLoaded, fetchAlerts]);

  // Animar apertura
  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: ALERT_CONFIG.ANIMATION_CONFIG.FADE_DURATION,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 100,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      fadeAnim.setValue(0);
      scaleAnim.setValue(0.8);
    }
  }, [visible, fadeAnim, scaleAnim]);

  const handleClose = useCallback(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.SCALE_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: ALERT_CONFIG.ANIMATION_CONFIG.SCALE_DURATION,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setSearchTerm("");
      setIsExpanded(false);
      setSelectedClientes(new Set());
      setSelectedTiposOrden(new Set());
      setIsClientesDropdownOpen(false);
      setIsTiposOrdenDropdownOpen(false);
      onClose();
    });
  }, [fadeAnim, scaleAnim, onClose]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleMarkCompleted = useCallback((mo, processCode) => {
    // Remover la alerta de la lista
    setAlerts((prev) => prev.filter(
      (alert) => !(alert.mo === mo && alert.processCode === processCode)
    ));
  }, []);

  const handleExportExcel = useCallback(() => {
    if (filteredAlerts.length === 0) {
      notify.info("No hay datos filtrados para exportar.");
      return;
    }

    try {
      const formattedData = filteredAlerts.map((row) => ({
        "MO": row.mo || "",
        "Cliente": row.cliente || "",
        "Fecha Pedido": row.fechaPedido 
          ? (typeof row.fechaPedido === "object" && row.fechaPedido.date 
              ? row.fechaPedido.date.split(" ")[0] 
              : row.fechaPedido)
          : "N/A",
        "Fecha Entrega": row.fechaEntrega 
          ? (typeof row.fechaEntrega === "object" && row.fechaEntrega.date 
              ? row.fechaEntrega.date.split(" ")[0] 
              : row.fechaEntrega)
          : "N/A",
        "Proceso": row.processName || "",
        "Código Proceso": row.processCode || "",
        "Días desde F. Pedido": row.daysSinceOrder !== undefined ? row.daysSinceOrder : 0,
        "Días desde Últ. Proceso": row.days !== undefined ? row.days : 0,
        "Días Máx. Proceso": row.expectedDays || 0,
        "Tipo Orden": row.tipo_orden || "N/A",
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData, {
        cellDates: true,
        dateNF: "dd/mm/yyyy",
      });

      // Configurar ancho de columnas
      worksheet['!cols'] = [
        { wch: 15 }, // MO
        { wch: 30 }, // Cliente
        { wch: 15 }, // Fecha Pedido
        { wch: 15 }, // Fecha Entrega
        { wch: 25 }, // Proceso
        { wch: 15 }, // Código Proceso
        { wch: 20 }, // Días desde F. Pedido
        { wch: 22 }, // Días desde Últ. Proceso
        { wch: 18 }, // Días Máx. Proceso
        { wch: 20 }, // Tipo Orden
      ];

      // Aplicar filtros automáticos
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Alertas Procesos");

      const fileName = `alertas_procesos_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      notify.success(
        `Se exportaron ${filteredAlerts.length} alertas filtradas a Excel.`
      );
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      notify.error(getErrorMessage(error, "No se pudo exportar el archivo Excel."));
    }
  }, [filteredAlerts]);

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      animationType='none'
      transparent={true}
      onRequestClose={handleClose}>
      <TouchableWithoutFeedback onPress={handleClose}>
        <Animated.View style={[styles.alertOverlay, { opacity: fadeAnim }]}>
          <TouchableWithoutFeedback onPress={() => {}}>
            <Animated.View
              style={[
                styles.alertModalContent,
                {
                  transform: [{ scale: scaleAnim }],
                  width: isExpanded ? "95%" : "92%",
                  maxWidth: 980,
                  maxHeight: isExpanded ? "85%" : "75%",
                },
              ]}>
              <LinearGradient
                colors={[ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_WHITE, ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_LIGHT]}
                style={styles.alertGradientContainer}>
                <View style={styles.alertHeader}>
                  <View style={styles.alertTitleContainer}>
                    <MaterialCommunityIcons
                      name='alert-circle'
                      size={20}
                        color={
                          alerts.length > 0
                            ? STATUS_COLORS.ERROR
                            : BACKGROUND_COLORS.BLUISH_GRAY
                        }
                    />
                    <View style={styles.alertTitleTextContainer}>
                      <Text style={styles.alertTitle}>
                        Alertas de Procesos
                      </Text>
                      <Text style={styles.alertSubtitle}>
                        {filteredAlerts.length === 0
                          ? "No hay alertas pendientes"
                          : (searchTerm.trim() || selectedClientes.size > 0 || selectedTiposOrden.size > 0)
                          ? `${filteredAlerts.length} de ${alerts.length} alerta${
                              alerts.length !== 1 ? "s" : ""
                            }`
                          : `${filteredAlerts.length} alerta${
                              filteredAlerts.length !== 1 ? "s" : ""
                            } pendiente${filteredAlerts.length !== 1 ? "s" : ""}`}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.alertActions}>
                    {alerts.length > 0 && (
                      <TouchableOpacity
                        onPress={handleExportExcel}
                        style={styles.actionButton}>
                        <MaterialCommunityIcons
                          name='download'
                          size={18}
                          color={BRAND_COLORS.ACCENT}
                        />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      onPress={toggleExpanded}
                      style={styles.actionButton}>
                      <MaterialCommunityIcons
                        name={
                          isExpanded ? "arrow-collapse" : "arrow-expand"
                        }
                        size={18}
                        color={BRAND_COLORS.ACCENT}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={handleClose}
                      style={styles.actionButton}>
                      <MaterialCommunityIcons
                        name='close'
                        size={18}
                        color={TEXT_COLORS.SECONDARY}
                      />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Filtros */}
                <View style={styles.filtersWrapper}>
                  <View style={styles.filtersRow}>
                    <FilterDropdown
                      label="Clientes"
                      options={clientesOptions}
                      selectedOptions={selectedClientes}
                      onToggleOption={handleToggleCliente}
                      onSelectAll={handleSelectAllClientes}
                      onDeselectAll={handleDeselectAllClientes}
                      onPrint={() => {}}
                      isOpen={isClientesDropdownOpen}
                      onToggleOpen={() => {
                        setIsClientesDropdownOpen(!isClientesDropdownOpen);
                        setIsTiposOrdenDropdownOpen(false);
                      }}
                    />
                    <FilterDropdown
                      label="Tipo Orden"
                      options={tiposOrdenOptions}
                      selectedOptions={selectedTiposOrden}
                      onToggleOption={handleToggleTipoOrden}
                      onSelectAll={handleSelectAllTiposOrden}
                      onDeselectAll={handleDeselectAllTiposOrden}
                      onPrint={() => {}}
                      isOpen={isTiposOrdenDropdownOpen}
                      onToggleOpen={() => {
                        setIsTiposOrdenDropdownOpen(!isTiposOrdenDropdownOpen);
                        setIsClientesDropdownOpen(false);
                      }}
                    />
                  </View>
                </View>

                {/* Buscador */}
                <View style={styles.searchContainer}>
                  <MaterialCommunityIcons
                    name='magnify'
                    size={20}
                    color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                    style={styles.searchIcon}
                  />
                  <TextInput
                    style={styles.searchInput}
                    placeholder="Buscar por MO, cliente, proceso..."
                    placeholderTextColor={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                    value={searchTerm}
                    onChangeText={setSearchTerm}
                  />
                  {searchTerm.length > 0 && (
                    <TouchableOpacity
                      onPress={() => setSearchTerm("")}
                      style={styles.clearButton}>
                      <MaterialCommunityIcons
                        name='close-circle'
                        size={20}
                        color={ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY}
                      />
                    </TouchableOpacity>
                  )}
                </View>

                {/* Tabla */}
                <View style={styles.tableContainer}>
                  {/* Header de la tabla */}
                  <View style={styles.tableHeader}>
                    <View style={styles.tableCellMO}>
                      <Text style={styles.headerText}>MO</Text>
                    </View>
                    <View style={styles.tableCellCliente}>
                      <Text style={styles.headerText}>Cliente</Text>
                    </View>
                    <View style={styles.tableCellFecha}>
                      <Text style={styles.headerText}>F. Pedido</Text>
                    </View>
                    <View style={styles.tableCellFechaEntrega}>
                      <Text style={styles.headerText}>F. Entrega</Text>
                    </View>
                    <View style={styles.tableCellProceso}>
                      <Text style={styles.headerText}>Proceso</Text>
                    </View>
                    <View style={styles.tableCellDiasPedido}>
                      <Text style={styles.headerText}>Días desde F. Pedido</Text>
                    </View>
                    <View style={styles.tableCellDiasUltimo}>
                      <Text style={styles.headerText}>Días desde Últ. Proceso</Text>
                    </View>
                    <View style={styles.tableCellDiasEsperados}>
                      <Text style={styles.headerText}>Días Máx. Proceso</Text>
                    </View>
                    <View style={styles.tableCellAction}>
                      <Text style={styles.headerText}>Acción</Text>
                    </View>
                  </View>

                  {/* Contenido de la tabla */}
                  <View style={styles.tableBody}>
                    {isLoading ? (
                      <View style={styles.loadingContainer}>
                        <Text style={styles.loadingText}>Cargando alertas...</Text>
                      </View>
                    ) : filteredAlerts.length > 0 ? (
                      <FlatList
                        data={filteredAlerts}
                        keyExtractor={(item, index) => `${item.mo}-${item.processCode}-${index}`}
                        renderItem={({ item }) => (
                          <ProcessAlertRow 
                            item={item} 
                            onMarkCompleted={handleMarkCompleted}
                            onRefresh={refreshAlerts}
                            userTeam={userTeam}
                            userName={userName}
                          />
                        )}
                        showsVerticalScrollIndicator={true}
                      />
                    ) : (
                      <View style={styles.emptyContainer}>
                        <Text style={styles.emptyText}>
                          {searchTerm.trim() 
                            ? "No se encontraron resultados" 
                            : "No hay alertas de procesos pendientes"}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </LinearGradient>
            </Animated.View>
          </TouchableWithoutFeedback>
        </Animated.View>
      </TouchableWithoutFeedback>
    </Modal>
  );
}

const styles = StyleSheet.create({
  alertOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(23, 64, 69, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },
  alertModalContent: {
    backgroundColor: ADMIN_SETTING_SCREEN_COLORS.BACKGROUND_CARD,
    borderRadius: 16,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    overflow: Platform.OS === 'web' ? "visible" : "hidden",
    boxShadow: `0 8px 16px ${SHADOW_COLORS.PRIMARY}33`,
    borderWidth: 1,
    borderColor: BORDER_COLORS.PRIMARY,
  },
  alertGradientContainer: {
    borderRadius: 16,
    overflow: Platform.OS === 'web' ? "visible" : "hidden",
    flex: 1,
  },
  filtersWrapper: {
    overflow: "visible",
    zIndex: 1000,
    position: "relative",
  },
  alertHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: BACKGROUND_COLORS.BLUISH_GRAY,
  },
  alertTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  alertTitleTextContainer: {
    marginLeft: 10,
    flex: 1,
  },
  alertTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: BRAND_COLORS.ACCENT,
  },
  alertSubtitle: {
    fontSize: 12,
    color: TEXT_COLORS.SECONDARY,
    marginTop: 2,
  },
  alertActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  actionButton: {
    padding: 6,
    marginLeft: 2,
    borderRadius: 6,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BACKGROUND_COLORS.SECONDARY,
    borderRadius: 8,
    marginHorizontal: 16,
    marginVertical: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: ADMIN_SETTING_SCREEN_COLORS.BORDER_SECONDARY,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    padding: 0,
  },
  clearButton: {
    marginLeft: 8,
    padding: 4,
  },
  tableContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: BRAND_COLORS.ACCENT,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
  },
  headerText: {
    fontSize: 12,
    fontWeight: "700",
    color: TEXT_COLORS.LIGHT,
    textTransform: "uppercase",
  },
  tableBody: {
    flex: 1,
    backgroundColor: BACKGROUND_COLORS.CARD,
    borderWidth: 1,
    borderColor: BORDER_COLORS.SECONDARY,
    borderTopWidth: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    maxHeight: 400,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BORDER_COLORS.SECONDARY,
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: "center",
    minHeight: 40,
  },
  tableCellMO: {
    flex: 1.2,
    paddingRight: 8,
    paddingLeft: 4,
    minWidth: 100,
  },
  tableCellCliente: {
    flex: 1.8,
    paddingRight: 8,
    minWidth: 150,
  },
  tableCellFecha: {
    flex: 1,
    paddingRight: 6,
    alignItems: "center",
    minWidth: 90,
  },
  tableCellFechaEntrega: {
    flex: 1,
    paddingRight: 6,
    alignItems: "center",
    minWidth: 90,
  },
  tableCellProceso: {
    flex: 1.3,
    paddingRight: 8,
    minWidth: 100,
  },
  tableCellDiasPedido: {
    flex: 1.1,
    paddingRight: 6,
    alignItems: "center",
    minWidth: 80,
  },
  tableCellDiasUltimo: {
    flex: 1.1,
    paddingRight: 6,
    alignItems: "center",
    minWidth: 80,
  },
  tableCellDiasEsperados: {
    flex: 1,
    paddingRight: 6,
    alignItems: "center",
    minWidth: 70,
  },
  tableCellAction: {
    width: 110,
    alignItems: "center",
  },
  moContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  priorityIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 6,
  },
  moText: {
    fontSize: 12,
    fontWeight: "600",
    color: BRAND_COLORS.ACCENT,
    fontFamily: "monospace",
  },
  cellText: {
    fontSize: 13,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    lineHeight: 18,
  },
  markCompletedButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: BACKGROUND_COLORS.SECONDARY,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: BRAND_COLORS.ACCENT,
  },
  markCompletedButtonDisabled: {
    backgroundColor: BACKGROUND_COLORS.TERTIARY,
    borderColor: BACKGROUND_COLORS.BLUISH_GRAY,
  },
  markCompletedText: {
    fontSize: 11,
    color: BRAND_COLORS.ACCENT,
    fontWeight: "500",
    marginLeft: 4,
  },
  markCompletedTextDisabled: {
    color: BACKGROUND_COLORS.BLUISH_GRAY,
  },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    fontSize: 14,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY,
  },
  emptyContainer: {
    padding: 20,
    alignItems: "center",
  },
  emptyText: {
    fontSize: 14,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_SECONDARY,
  },
  filtersRow: {
    flexDirection: "row",
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 8,
    gap: 12,
    zIndex: 1000,
    position: "relative",
  },
  filterContainer: {
    flex: 1,
    position: "relative",
    zIndex: 1000,
  },
  filterButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: BACKGROUND_COLORS.SECONDARY,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: ADMIN_SETTING_SCREEN_COLORS.BORDER_SECONDARY,
    minHeight: 40,
  },
  filterButtonText: {
    fontSize: 14,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    fontWeight: "500",
    flex: 1,
  },
  filterBadge: {
    backgroundColor: BRAND_COLORS.ACCENT,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 8,
    minWidth: 20,
    alignItems: "center",
  },
  filterBadgeText: {
    color: TEXT_COLORS.LIGHT,
    fontSize: 11,
    fontWeight: "600",
  },
  filterDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: BACKGROUND_COLORS.CARD,
    borderRadius: 8,
    marginTop: 4,
    borderWidth: 1,
    borderColor: ADMIN_SETTING_SCREEN_COLORS.BORDER_SECONDARY,
    maxHeight: 300,
    shadowColor: SHADOW_COLORS.PRIMARY,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    zIndex: 1001,
    overflow: "visible",
    ...(Platform.OS === 'web' && {
      position: "absolute",
      zIndex: 1001,
    }),
  },
  filterActions: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BORDER_COLORS.SECONDARY,
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 8,
  },
  filterActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: BACKGROUND_COLORS.SECONDARY,
    borderWidth: 1,
    borderColor: BRAND_COLORS.ACCENT,
  },
  filterActionText: {
    fontSize: 11,
    color: BRAND_COLORS.ACCENT,
    fontWeight: "500",
  },
  filterOptions: {
    maxHeight: 250,
  },
  filterOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: BACKGROUND_COLORS.TERTIARY,
  },
  filterOptionText: {
    fontSize: 13,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    marginLeft: 8,
    flex: 1,
  },
  filterOptionTextSelected: {
    fontWeight: "600",
    color: BRAND_COLORS.ACCENT,
  },
  filterSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BACKGROUND_COLORS.PRIMARY,
    borderRadius: 6,
    marginHorizontal: 8,
    marginTop: 8,
    marginBottom: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: BORDER_COLORS.SECONDARY,
  },
  filterSearchIcon: {
    marginRight: 8,
  },
  filterSearchInput: {
    flex: 1,
    fontSize: 13,
    color: ADMIN_SETTING_SCREEN_COLORS.TEXT_DARK,
    padding: 0,
  },
  filterClearButton: {
    marginLeft: 8,
    padding: 2,
  },
});
